import React, { useEffect, useState, useMemo } from 'react';
import { Field, getFormValues, reduxForm } from 'redux-form';
import { Row, Col, Container } from 'reactstrap';
import LinkVariantIcon from 'mdi-react/LinkVariantIcon';
import moment from 'moment';
import TimetableIcon from 'mdi-react/TimetableIcon';
import renderField from '../../../shared/components/custom/Field';
import renderDropZoneField from '../../../shared/components/form/DropZoneMS';
import validateQuestionaireForm from './validateQuestionaireForm';
import renderSwitchField from '../../../shared/components/custom/Switch';
import { partial } from '../../../util/translation/translation';
import { getImageHash } from '../../../util/functions';
import renderSelectField from '../../../shared/components/form/Select';
import TextEditor2 from '../../../shared/components/text-editor/TextEditor2';
import { connect } from 'react-redux';
import TooltipWithIcon from '../../../shared/components/custom/TooltipWithIcon';
import Checkbox from '../../../shared/components/custom/Checkbox';
import FormBox from '../../../shared/components/custom/FormBox';

const QuestionaireForm = ({
  questionaire,
  handleSubmit,
  initialize,
  questionaireCategories,
  change,
  leadCompanies,
  questionaireFormValues,
}) => {
  const s = partial('shared');
  const p = partial('questionaire');
  const q = partial('question');
  const [questionaireType, setQuestionaireType] = useState(
    questionaire?.type || 'QUESTIONAIRE'
  );
  const questionaireTypes = ['QUESTIONAIRE', 'QUIZ'].map((item) => ({
    label: p(`questionaireType-${item}`),
    value: item,
  }));
  const questionaireCategoriesOptions = useMemo(
    () =>
      questionaireCategories.map((item) => ({
        label: item.title,
        value: item.id,
      })),
    [questionaireCategories]
  );

  const leadCompaniesOptions = useMemo(
    () =>
      leadCompanies.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [leadCompanies]
  );
  const answerVisibilityOptions = [
    { label: p('answerVisibility-NOT_VISIBLE'), value: 'NOT_VISIBLE' },
    {
      label: p('answerVisibility-SHOW_DURING_QUIZ'),
      value: 'SHOW_DURING_QUIZ',
    },
    {
      label: p('answerVisibility-SHOW_AFTER_ANSWERED'),
      value: 'SHOW_AFTER_ANSWERED',
    },
    {
      label: p('answerVisibility-SHOW_AFTER_ENDED'),
      value: 'SHOW_AFTER_ENDED',
    },
  ];

  useEffect(() => {
    if (questionaire) {
      setQuestionaireType(questionaire.type);
      initialize({
        headingImage: questionaire.imageUrl
          ? {
              name: 'questionaire image',
              preview: questionaire.imageUrl,
              croppedImage: questionaire.imageUrl,
              hash: getImageHash(questionaire.imageUrl),
            }
          : undefined,
        title: questionaire.title,
        description: questionaire.description,
        visible: !!questionaire.visible,
        editAnswers: !!questionaire.editAnswers,
        startDate: moment(questionaire.startDate).format('YYYY-MM-DDTHH:mm'),
        endDate: moment(questionaire.endDate).format('YYYY-MM-DDTHH:mm'),
        type: {
          label: p(`questionaireType-${questionaire.type}`),
          value: questionaire.type,
        },
        dynamicLink: questionaire.dynamicLink,
        subtitle: questionaire.subtitle,
        category: questionaireCategoriesOptions.find(
          (item) => item.value === questionaire?.category?.id
        ),
        leadCompany: leadCompaniesOptions.find(
          (item) => item.value === questionaire?.leadCompany?.id
        ),
        answerVisibility: answerVisibilityOptions.find(
          (item) => item.value === questionaire?.answerVisibility
        ),
        ownerEmail: questionaire?.ownerEmail?.email,
      });
    }
  }, [questionaire, questionaireCategories, leadCompaniesOptions]);
  return (
    <form className="form" onSubmit={handleSubmit}>
      <Container>
        <Row>
          <Col lg={6}>
            <div
              style={{
                backgroundColor: '#F4F4F6',
                borderRadius: 4,
                borderColor: '#D7DAE0',
                borderStyle: 'solid',
                borderWidth: 1,
                marginBottom: 10,
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottomWidth: 1,
                  borderBottomStyle: 'dashed',
                  borderColor: '#D7DAE0',
                  padding: '10px 10px 10px 10px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      fontSize: 16,
                      lineHeight: '24px',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    {s('details')}
                  </div>
                </div>
              </div>
              <div style={{ padding: '10px' }}>
                <Container>
                  <Row>
                    <Col>
                      <div className="form__form-group">
                        <span className="form-header">{s('title')}*</span>
                        <Field
                          name="title"
                          component={renderField}
                          placeholder={s('title')}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="form__form-group">
                        <span className="form-header">{s('subtitle')}*</span>
                        <Field
                          name="subtitle"
                          component={renderField}
                          placeholder={s('subtitle')}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="form__form-group">
                        <span className="form-header">
                          {p('questionaireCategories.questionaireCategory')}
                        </span>
                        <Field
                          name="category"
                          placeholder={p(
                            'questionaireCategories.selectCategory'
                          )}
                          options={questionaireCategoriesOptions}
                          component={renderSelectField}
                        />
                      </div>
                    </Col>
                  </Row>
                  {leadCompaniesOptions.length > 0 && (
                    <Row>
                      <Col>
                        <div className="form__form-group">
                          <span className="form-header">
                            {p('leadCompany')}
                          </span>
                          <Field
                            name="leadCompany"
                            placeholder={p('leadCompany')}
                            options={leadCompaniesOptions}
                            component={renderSelectField}
                            onChange={(e) => {
                              change('editAnswers', false);
                              change('answerVisibility', {
                                label: p('answerVisibility-NOT_VISIBLE'),
                                value: 'NOT_VISIBLE',
                              });
                            }}
                          />
                        </div>
                      </Col>
                    </Row>
                  )}

                  <Row>
                    <Col>
                      <div className="form__form-group">
                        <span className="form-header">{s('description')}*</span>
                        <Field
                          name="description"
                          component={TextEditor2}
                          header={[2, 3, false]}
                          placeholder={s('description')}
                        />
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="form__form-group">
                        <span className="form-header">{p('selectType')}</span>
                        <div className="form__form-group-field">
                          <Field
                            name="type"
                            placeholder={p('selectType')}
                            options={questionaireTypes}
                            component={renderSelectField}
                            disabled={questionaire?.id}
                            onChange={(e) => {
                              setQuestionaireType(e.value);
                              if (e.value === 'QUIZ') {
                                change('editAnswers', false);
                              }
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="form__form-group">
                        <span className="form">{s('startDate')}*</span>
                        <div className="form__form-group-field">
                          <div className="form__form-group-icon">
                            <TimetableIcon />
                          </div>
                          <Field
                            name="startDate"
                            component={renderField}
                            type="datetime-local"
                          />
                        </div>
                      </div>
                    </Col>
                    <Col>
                      <div className="form__form-group">
                        <span className="form">{s('endDate')}*</span>
                        <div className="form__form-group-field">
                          <div className="form__form-group-icon">
                            <TimetableIcon />
                          </div>
                          <Field
                            name="endDate"
                            component={renderField}
                            type="datetime-local"
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </div>
            </div>
          </Col>
          <Col lg={6}>
            <div
              style={{
                backgroundColor: '#F4F4F6',
                borderRadius: 4,
                borderColor: '#D7DAE0',
                borderStyle: 'solid',
                borderWidth: 1,
                marginBottom: 10,
                width: '100%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  borderBottomWidth: 1,
                  borderBottomStyle: 'dashed',
                  borderColor: '#D7DAE0',
                  padding: '10px 10px 10px 10px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      fontSize: 16,
                      lineHeight: '24px',
                      color: 'black',
                      fontWeight: 'bold',
                    }}
                  >
                    {s('image')}
                  </div>
                </div>
              </div>
              <div style={{ padding: '10px' }}>
                <div className="form__form-group">
                  <Field
                    saveBase64Cropped
                    name="headingImage"
                    maxSize={3}
                    component={renderDropZoneField}
                    displayCrop
                    crop="BANNER_CROP"
                  />
                  <span className="form-header">
                    <strong>{s('note')}:</strong> {s('recommendedImageSize')}{' '}
                    16:9 (1035 x 570 px)
                  </span>
                </div>
              </div>
            </div>
            <FormBox style={{ marginBottom: 8 }} title={p('notifyWhenAnsered')}>
              <Col>
                <Row>
                  <Col>
                    <span className="form">
                      {s('email')}&nbsp;
                      <TooltipWithIcon
                        id="question-owner-email"
                        text={p('notifyWhenAnseredInfo')}
                      />
                    </span>
                    <div className="form__form-group">
                      <Field
                        name="ownerEmail"
                        component={renderField}
                        placeholder={s('email')}
                      />
                    </div>
                  </Col>
                  {/*<Col>
                      <div className="form__form-group flex-Vcenter">
                        <Field
                          name="answerEdit"
                          component={renderSwitchField}
                          label={[
                            'Include answer edits',
                            'Exclude answer edits',
                          ]}
                          className="colored-click"
                        />{' '}
                        &nbsp;
                        <TooltipWithIcon
                          id="question-owner-email-answers"
                          text="Emails will be recieved for updated to answers when checked"
                        />
                      </div>
                    </Col>*/}
                </Row>
              </Col>
            </FormBox>
            <FormBox style={{ marginBottom: 8 }} title={q('options')}>
              <Col>
                <div
                  className="form__form-group flex-Vcenter"
                  style={{ margin: '9px' }}
                >
                  <Field
                    name="visible"
                    component={renderSwitchField}
                    label={[`${s('active')}`, `${s('inActive')}`]}
                    defaultChecked
                    className="colored-click"
                  />
                </div>
              </Col>
              <Col>
                <div
                  className="form__form-group flex-Vcenter"
                  style={{ margin: '9px' }}
                >
                  <Field
                    name="editAnswers"
                    component={renderSwitchField}
                    disabled={
                      questionaireType === 'QUIZ' ||
                      !!questionaireFormValues?.leadCompany
                    }
                    label={[p('editAnswers'), p('notEditAnswers')]}
                    defaultChecked
                    className="colored-click"
                  />
                </div>
              </Col>
              <Col>
                <div
                  className="form__form-group flex-Vcenter"
                  style={{ margin: '9px' }}
                >
                  <Field
                    name="leaderBoardVisibility"
                    component={renderSwitchField}
                    disabled={questionaireType === 'QUESTIONAIRE'}
                    label={['Show leaderboard', 'Hide leaderboard']}
                    defaultChecked
                    className="colored-click"
                  />
                </div>
              </Col>
              <Col>
                <div className="form__form-group">
                  <span className="form-header">{p('answerVisibility')}*</span>
                  <Field
                    name="answerVisibility"
                    placeholder={p('selectAnswerVisibility')}
                    options={answerVisibilityOptions}
                    defaultValue={answerVisibilityOptions[0]}
                    disabled={!!questionaireFormValues?.leadCompany}
                    component={renderSelectField}
                  />
                </div>
              </Col>
            </FormBox>
            {questionaire ? (
              // <Row>
              <Col>
                <div className="form__form-group">
                  <span className="form-header marginRight-8">
                    {s('universalLink')}
                  </span>
                  <div className="form__form-group-field">
                    <Field name="dynamicLink" component="input" disabled />
                    <button
                      type="button"
                      className="form__form-group-icon"
                      onClick={() =>
                        navigator.clipboard.writeText(questionaire.dynamicLink)
                      }
                    >
                      <LinkVariantIcon />
                    </button>
                  </div>
                </div>
              </Col>
            ) : // </Row>
            null}
          </Col>
        </Row>
      </Container>
    </form>
  );
};

const reduxF = reduxForm({
  form: 'questionaire_form',
  validate: validateQuestionaireForm,
});

const mapStateToProps = (state) => ({
  questionaireFormValues: getFormValues('questionaire_form')(state),
});

export default connect(mapStateToProps)(reduxF(QuestionaireForm));
